import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

export default function Carousel({
  items = [],
  itemsPerPage = 2,
  autoplayMs = 5000,
  scrollDuration = 40,
  renderItem,
  pauseOnHover = true,
  viewportClassName = "",
}) {
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    dragFree: false,
    slidesToScroll: Math.max(1, itemsPerPage),
    duration: scrollDuration,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const goTo = useCallback(
    (index) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const next = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const prev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onInit);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onInit);
    };
  }, [emblaApi, onInit, onSelect]);

  // autoplay engine
  useEffect(() => {
    if (!emblaApi) return;
    if (paused || autoplayMs <= 0 || scrollSnaps.length <= 1) return;

    timerRef.current = setInterval(() => {
      emblaApi.scrollNext();
    }, autoplayMs);

    return () => clearInterval(timerRef.current);
  }, [paused, autoplayMs, emblaApi, scrollSnaps.length]);

  return (
    <div
      onMouseEnter={() => pauseOnHover && setPaused(true)}
      onMouseLeave={() => pauseOnHover && setPaused(false)}
    >
      <div className={`overflow-hidden ${viewportClassName}`} ref={emblaRef}>
        <div className="flex -mx-3">
          {items.map((item, i) => (
            <div
              key={item?.id ?? `slide-${i}`}
              className="min-w-0 px-3"
              style={{ flex: `0 0 ${100 / itemsPerPage}%` }}
            >
              {renderItem(item, i)}
            </div>
          ))}
        </div>
      </div>

      {scrollSnaps.length > 1 && (
        <div className="flex items-center justify-center gap-3 mt-10">
          <button
            onClick={prev}
            className="w-9 h-9 flex items-center justify-center border border-[var(--dvx-border)] text-[var(--dvx-text)] hover:border-[var(--dvx-primary)] hover:text-[var(--dvx-primary)] transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft size={16} />
          </button>

          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`h-1 transition-all duration-300 ${
                i === selectedIndex
                  ? "w-8 bg-[var(--dvx-primary)]"
                  : "w-4 bg-[var(--dvx-border)]"
              }`}
              aria-label={`Page ${i + 1}`}
            />
          ))}

          <button
            onClick={next}
            className="w-9 h-9 flex items-center justify-center border border-[var(--dvx-border)] text-[var(--dvx-text)] hover:border-[var(--dvx-primary)] hover:text-[var(--dvx-primary)] transition-colors"
            aria-label="Next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
